import * as React from 'react';
import { cn } from '@/lib/utils';
import { CDCurrency } from '../primitives/CDCurrency';
import { CDEditableText } from '../primitives/CDEditableText';
import { CDLineNumber } from '../primitives/CDLineNumber';
import { CDSectionHeader } from '../primitives/CDSectionHeader';

export interface TransactionLineItem {
  lineNumber: number | string;
  description: string;
  amount?: number;
}

export interface TransactionSection {
  title: string;
  items: TransactionLineItem[];
  adjustments?: TransactionLineItem[];
  subtotal?: number;
}

export interface BorrowerTransactionData {
  dueToBorrower: TransactionSection; // K
  paidAlreadyByBorrower: TransactionSection; // L
  cashToClose: number; // M - Calculation
  cashDirection: 'from' | 'to'; // From Borrower or To Borrower
}

export interface SellerTransactionData {
  dueToSeller: TransactionSection; // N
  dueFromSeller: TransactionSection; // O
  cashFromSeller: number; // P - Calculation
  cashDirection: 'from' | 'to'; // From Seller or To Seller
}

export interface SummariesOfTransactionsData {
  borrower: Partial<BorrowerTransactionData>;
  seller: Partial<SellerTransactionData>;
}

export interface CDSummariesOfTransactionsProps {
  /** Summaries data */
  data: Partial<SummariesOfTransactionsData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** onChange handler */
  onChange?: (data: Partial<SummariesOfTransactionsData>) => void;
  /** Additional className */
  className?: string;
}

/**
 * CDSummariesOfTransactions - Summaries of Transactions on Page 3
 *
 * Side-by-side display of:
 * - Borrower's Transaction (K, L, M)
 * - Seller's Transaction (N, O, P)
 */
export const CDSummariesOfTransactions = React.forwardRef<
  HTMLDivElement,
  CDSummariesOfTransactionsProps
>(({ data, editable = false, onChange, className }, ref) => {
  // Helper to update borrower section items
  const updateBorrowerItem = (
    sectionKey: 'dueToBorrower' | 'paidAlreadyByBorrower',
    itemType: 'items' | 'adjustments',
    itemIndex: number,
    updates: Partial<TransactionLineItem>,
  ) => {
    const borrower = data.borrower || {};
    const section = borrower[sectionKey] || { title: '', items: [] };
    const items = section[itemType] || [];
    const updatedItems = items.map((item, idx) =>
      idx === itemIndex ? { ...item, ...updates } : item,
    );
    onChange?.({
      ...data,
      borrower: {
        ...borrower,
        [sectionKey]: { ...section, [itemType]: updatedItems },
      },
    });
  };

  // Helper to update seller section items
  const updateSellerItem = (
    sectionKey: 'dueToSeller' | 'dueFromSeller',
    itemType: 'items' | 'adjustments',
    itemIndex: number,
    updates: Partial<TransactionLineItem>,
  ) => {
    const seller = data.seller || {};
    const section = seller[sectionKey] || { title: '', items: [] };
    const items = section[itemType] || [];
    const updatedItems = items.map((item, idx) =>
      idx === itemIndex ? { ...item, ...updates } : item,
    );
    onChange?.({
      ...data,
      seller: {
        ...seller,
        [sectionKey]: { ...section, [itemType]: updatedItems },
      },
    });
  };

  const renderLineItems = (
    items: TransactionLineItem[] = [],
    onItemChange?: (idx: number, updates: Partial<TransactionLineItem>) => void,
  ) => {
    return items.map((item, idx) => (
      <tr key={idx} className="cd-text-small">
        <td className="border border-cd-black px-1 py-0.5 w-6 text-right">
          <CDLineNumber number={item.lineNumber} />
        </td>
        <td className="border border-cd-black px-1 py-0.5">
          <CDEditableText
            value={item.description}
            editable={editable}
            onChange={(v) => onItemChange?.(idx, { description: v })}
            size="small"
          />
        </td>
        <td className="border border-cd-black px-1 py-0.5 text-right w-20">
          <CDCurrency
            value={item.amount}
            editable={editable}
            onChange={(v) => onItemChange?.(idx, { amount: v })}
            className="cd-text-small"
          />
        </td>
      </tr>
    ));
  };

  const renderSection = (
    section: TransactionSection | undefined,
    identifier: string,
    showAdjustmentsLabel = false,
    onItemChange?: (
      itemType: 'items' | 'adjustments',
      idx: number,
      updates: Partial<TransactionLineItem>,
    ) => void,
  ) => {
    if (!section) return null;
    return (
      <>
        <tr>
          <td colSpan={3} className="p-0">
            <div className="cd-subsection-header">
              <span className="cd-subsection-header-letter">
                {identifier}. {section.title}
              </span>
              <span className="cd-subsection-header-amount">
                <CDCurrency value={section.subtotal} className="cd-text-small font-bold" />
              </span>
            </div>
          </td>
        </tr>
        {renderLineItems(section.items, (idx, updates) => onItemChange?.('items', idx, updates))}
        {section.adjustments && section.adjustments.length > 0 && (
          <>
            {showAdjustmentsLabel && (
              <tr className="cd-text-tiny">
                <td
                  colSpan={3}
                  className="border border-cd-black px-1 py-0.5 italic text-cd-gray-text"
                >
                  Adjustments for Items Paid by Seller in Advance
                </td>
              </tr>
            )}
            {renderLineItems(section.adjustments, (idx, updates) =>
              onItemChange?.('adjustments', idx, updates),
            )}
          </>
        )}
      </>
    );
  };

  return (
    <div ref={ref} className={cn('', className)}>
      <CDSectionHeader title="Summaries of Transactions" />

      <div className="grid grid-cols-2 border-t-0">
        {/* Borrower's Transaction */}
        <div>
          <div className="cd-subsection-header border-t-0 font-bold">BORROWER'S TRANSACTION</div>

          <table className="w-full border-collapse">
            <tbody>
              {renderSection(data.borrower?.dueToBorrower, 'K', false, (itemType, idx, updates) =>
                updateBorrowerItem('dueToBorrower', itemType, idx, updates),
              )}
              {renderSection(
                data.borrower?.paidAlreadyByBorrower,
                'L',
                false,
                (itemType, idx, updates) =>
                  updateBorrowerItem('paidAlreadyByBorrower', itemType, idx, updates),
              )}

              {/* M. Calculation */}
              <tr className="cd-text-small font-bold bg-cd-gray-light">
                <td className="border border-cd-black border-t-2 px-1 py-1" colSpan={2}>
                  <div className="font-bold">CASH TO CLOSE</div>
                  <div className="cd-text-tiny font-normal">
                    {data.borrower?.cashDirection === 'to' ? 'To Borrower' : 'From Borrower'}
                  </div>
                </td>
                <td className="border border-cd-black border-t-2 px-1 py-1 text-right">
                  <CDCurrency
                    value={Math.abs(data.borrower?.cashToClose || 0)}
                    className="text-[11pt] font-bold"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Seller's Transaction */}
        <div>
          <div className="cd-subsection-header border-t-0 border-l-0 font-bold">
            SELLER'S TRANSACTION
          </div>

          <table className="w-full border-collapse">
            <tbody>
              {renderSection(data.seller?.dueToSeller, 'N', true, (itemType, idx, updates) =>
                updateSellerItem('dueToSeller', itemType, idx, updates),
              )}
              {renderSection(data.seller?.dueFromSeller, 'O', false, (itemType, idx, updates) =>
                updateSellerItem('dueFromSeller', itemType, idx, updates),
              )}

              {/* P. Calculation */}
              <tr className="cd-text-small font-bold bg-cd-gray-light">
                <td className="border border-cd-black border-l-0 border-t-2 px-1 py-1" colSpan={2}>
                  <div className="font-bold">CASH</div>
                  <div className="cd-text-tiny font-normal">
                    {data.seller?.cashDirection === 'from' ? 'From Seller' : 'To Seller'}
                  </div>
                </td>
                <td className="border border-cd-black border-t-2 px-1 py-1 text-right">
                  <CDCurrency
                    value={Math.abs(data.seller?.cashFromSeller || 0)}
                    className="text-[11pt] font-bold"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
});

CDSummariesOfTransactions.displayName = 'CDSummariesOfTransactions';
